import { Button } from "@client/components/Button";
import { Modal } from "@client/components/Modal";
import { AdoptionPanel } from "@client/components/editor/AdoptionPanel";
import type { EditorPanelKey } from "@client/components/editor/EditorToolbar";
import { LineItemsPanel } from "@client/components/editor/LineItemsPanel";
import { PackagesPanel } from "@client/components/editor/PackagesPanel";
import { ScenarioSettingsPanel } from "@client/components/editor/ScenarioSettingsPanel";
import type { DraftAction, EditorModel } from "@client/lib/scenarioDraft";

/**
 * One dialog for whichever group of inputs the rail asked for.
 *
 * The four panels are unchanged — they render `bare`, so the dialog's own `h2`
 * is the only heading and the card chrome is the dialog's. Editing inside a
 * dialog changes nothing about the draft: the reducer and the projection are the
 * page's, so the KPI strip behind the overlay still moves on every keystroke,
 * which is the reason the dialog is a dialog and not a route.
 */

const TITLES: Record<EditorPanelKey, { title: string; description: string }> = {
  settings: {
    title: "Scenario settings",
    description: "How far ahead this projection runs, and the rules for every package in it.",
  },
  packages: {
    title: "Packages",
    description: "What you sell, and what each tier costs to deliver.",
  },
  lines: {
    title: "Costs & revenues",
    description:
      "Everything outside the package price list — what you build, host, staff and sell on top.",
  },
  adoption: {
    title: "Adoption & churn",
    description: "Who signs each year, which package they take, and who leaves.",
  },
};

export function EditorPanelDialog({
  panel,
  onClose,
  model,
  dispatch,
  disabled,
}: {
  panel: EditorPanelKey | null;
  onClose: () => void;
  model: EditorModel;
  dispatch: (action: DraftAction) => void;
  disabled: boolean;
}) {
  if (panel === null) return null;

  const { title, description } = TITLES[panel];
  const props = { model, dispatch, disabled, bare: true } as const;

  return (
    <Modal
      open
      size="xl"
      title={title}
      description={description}
      onClose={onClose}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Done
        </Button>
      }
    >
      {panel === "settings" && <ScenarioSettingsPanel {...props} />}
      {panel === "packages" && <PackagesPanel {...props} />}
      {panel === "lines" && <LineItemsPanel {...props} />}
      {panel === "adoption" && <AdoptionPanel {...props} />}
    </Modal>
  );
}
